import type {ReactElement} from 'react';
import {useCallback, useState} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {useIsSmallerThanBreakpointWidth} from '@Hooks/useIsSmallerThanBreakpoint';
import {ScreenBreakpoints} from '@Libraries/responsiveness-library';
import type {AiChatBannerProps} from './ai-chat-banner.types';
import {AI_CHAT_BANNER_DISMISSED_KEY} from './ai-chat-banner.types';
import styles from './ai-chat-banner.module.scss';

export function AiChatBanner({banner}: AiChatBannerProps): ReactElement | null {
  const isSmallScreen = useIsSmallerThanBreakpointWidth(ScreenBreakpoints.SMALL);
  const [isDismissed, setIsDismissed] = useState(
    () => localStorage.getItem(AI_CHAT_BANNER_DISMISSED_KEY) === String(banner.version)
  );

  const bannerRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      document.documentElement.style.setProperty('--ai-chat-banner-height', `${node.offsetHeight}px`);
    } else {
      document.documentElement.style.setProperty('--ai-chat-banner-height', '0px');
    }
  }, []);

  if (isDismissed) {
    return null;
  }

  const onDismiss = (): void => {
    localStorage.setItem(AI_CHAT_BANNER_DISMISSED_KEY, String(banner.version));
    setIsDismissed(true);
  };

  const iconSize = isSmallScreen ? IconSize.SIZE_ICON_12 : IconSize.SIZE_ICON_16;
  const textSize = isSmallScreen ? TextSize.XXSMALL : TextSize.XSMALL;

  return (
    <div ref={bannerRef} className={styles.banner}>
      <div className={styles.spacer} />
      <div className={styles.content}>
        <Icon icon="icon-exclamation-triangle" size={iconSize} type={IconType.DANGER} className={'spacing-m-r-1 flex-vertical-center'} />
        <Text val={banner.message} size={textSize} className={`flex-vertical-center ${styles.message}`} />
      </div>
      <div className={styles.spacer}>
        <Icon
          icon="icon-close"
          size={iconSize}
          type={IconType.GHOST}
          className={styles.closeButton}
          isUsedAsButton
          onClick={onDismiss}
        />
      </div>
    </div>
  );
}
